// items-cs.js — Probe item bank: COMPUTER SCIENCE (leaf-shaped domain)
// =============================================================================
// One probe per CS node, keyed to the REAL registry node ids in graph-data.js.
//
// TOPOLOGY (why this domain matters for the engine). Unlike the math spine and
// the chemistry chain — both long, favourable up-propagation chains — CS is
// LEAF-SHAPED and cross-linked into other domains:
//     programming  <- algebra(soft), logic(soft)          [shallow entry]
//     data_structures <- programming(hard)
//     algorithms   <- data_structures(hard), logic(hard)
//     machine_learning <- linear_algebra(hard), statistics(hard),
//                         algorithms(soft), information_theory, cognitive_science, ethics
//     networking   <- programming(hard), systems_thinking, information_theory
// machine_learning is a CONFLUENCE node: it sits below linear_algebra AND
// statistics (both math) as HARD prereqs. That makes it the useful stress case
// here — a correct ML answer up-propagates into the MATH spine across a domain
// seam, and (conversely) a math gap should cap ML from below. This is the
// mirror image of the chemistry chain test: chemistry probed deep-chain lift;
// CS probes confluence lift and shallow-entry behaviour.
//
// AUTHORING STANDARD (identical to items-math.js / items-physics.js /
// items-chemistry.js — every probe must satisfy all five):
//   1. DIAGNOSTIC, not trivia. A correct answer must require understanding the
//      node's core idea, not recall of a keyword or a lucky guess.
//   2. ONE concept. Each probe targets the single most load-bearing idea, so a
//      miss cleanly implicates THAT node (and, via the DAG, its descendants).
//   3. PREREQUISITE-CLEAN. Answering must not secretly require a HARDER topic.
//      A data_structures probe must not need algorithmic complexity analysis;
//      an ML probe must not require doing the linear algebra by hand. We probe
//      the CONCEPT the node adds, never its downstream machinery.
//   4. DISTRACTORS ARE DIAGNOSTIC. Wrong options encode the specific common
//      misconception for that topic (noted per item in `trap`), so a wrong
//      answer is informative, not noise.
//   5. STABLE & UNAMBIGUOUS. Exactly one defensible answer; no "it depends."
//
// Schema per item (merged onto the registry node at load time):
//   { q, opts:[4], a:index, concept, trap }  — a = index of the correct option.
//   concept = the one idea being tested (authoring review + future tutor).
//   trap    = what choosing a wrong distractor reveals (authoring rationale;
//             not shown to the learner).
// =============================================================================

export const CS_ITEMS = {

  // --- SHALLOW ENTRY (prereq: algebra soft, logic soft) ----------------------
  programming: {
    q: 'A program runs:  x = 5 ;  y = x ;  x = 9 .  After it finishes, what is the value of y ?',
    opts: ['9', '5', '14', 'x'],
    a: 1,
    concept: 'Assignment copies the VALUE at the moment it runs; y = x binds y to 5, and a later change to x does not retroactively update y. This is the core mental model of variables and assignment.',
    trap: 'Picking 9 = believing y is an alias/reference that tracks x (the single most common novice mental-model error). Picking "x" = thinking y stores the variable itself rather than its value. Both reveal a real gap in how assignment works, not a slip. Prereq-clean: no algorithm, data structure, or math beyond reading two statements.'
  },

  // --- SECOND TIER (prereq: programming hard) --------------------------------
  data_structures: {
    q: 'You must repeatedly check whether a given name is already in a collection of a million names, as fast as possible. Which structure gives the best average lookup time?',
    opts: [
      'An unsorted array (list) scanned front to back',
      'A hash table (hash map / dictionary)',
      'A linked list',
      'A stack'
    ],
    a: 1,
    concept: 'Choosing a data structure by its access pattern: a hash table gives average O(1) membership lookup, versus O(n) for scanning an array or linked list. The idea is that structure determines cost, not just storage.',
    trap: 'Picking the unsorted array or linked list = treating all "collections" as interchangeable containers and missing that lookup cost depends on organization — the defining insight of the topic. Prereq-clean: tests the SELECTION principle, not complexity proofs (that is algorithms) — "average lookup time" is given in plain terms, no big-O required to answer.'
  },

  // --- THIRD TIER (prereq: data_structures hard, logic hard) -----------------
  algorithms: {
    q: 'Binary search finds an item in a sorted list of 1,000,000 elements. In the WORST case, roughly how many elements does it examine?',
    opts: ['about 1,000,000', 'about 500,000', 'about 20', 'exactly 1'],
    a: 2,
    concept: 'Logarithmic scaling: halving the search space each step means ~log2(N) comparisons (log2 of a million ≈ 20). The load-bearing idea is that a good algorithm changes the GROWTH RATE of work, not just the constant.',
    trap: 'Picking 1,000,000 = confusing binary search with a linear scan (no grasp of divide-and-conquer). Picking 500,000 = thinking "halved" means one halving, not repeated halving — a partial-understanding error. Both cleanly implicate algorithmic thinking. Prereq-clean: needs the sorted-list + halving idea, not code.'
  },

  // --- CONFLUENCE (prereq: linear_algebra hard, statistics hard, algorithms soft) ---
  machine_learning: {
    q: 'A model scores 99% accuracy on its training data but only 62% on new, unseen data. What has most likely happened?',
    opts: [
      'The model has underfit and needs to be simpler',
      'The model has overfit — memorized the training data instead of learning general patterns',
      'The new data is wrong and should be discarded',
      'Accuracy is not a meaningful measure here'
    ],
    a: 1,
    concept: 'Generalization vs. memorization: the train/test gap is the signature of overfitting — the central failure mode ML is organized around detecting and preventing. Learning means performing on UNSEEN data, not reproducing the training set.',
    trap: 'Picking "underfit" = inverting the concept (underfitting shows LOW training accuracy too). Picking "discard the new data" = the anti-scientific instinct to protect the model rather than trust held-out evaluation. This probes the concept ML ADDS, and stays clean of the linear-algebra / statistics machinery underneath (no gradients or distributions required to answer).'
  },

  // --- LEAF (prereq: programming hard, systems_thinking, information_theory) --
  networking: {
    q: 'When you request a web page, your data is broken into small "packets" that travel the internet independently and may arrive out of order. Why is the network designed this way rather than sending one continuous stream down a fixed line?',
    opts: [
      'To keep every message perfectly in order at all times',
      'So packets can take different routes and share links — making the network robust to failures and efficient under shared load',
      'Because computers cannot send large files',
      'To encrypt the data automatically'
    ],
    a: 1,
    concept: 'Packet switching vs. circuit switching: splitting data into independently-routed packets is what lets the internet share links among many users and reroute around failures. The core idea is decentralized, fault-tolerant routing over shared infrastructure.',
    trap: 'Picking "keep everything in order" = missing the whole point (ordering is reassembled at the destination precisely BECAUSE packets travel independently). Picking "cannot send large files" or "encrypt automatically" = conflating packetization with unrelated concerns. Prereq-clean: tests the switching CONCEPT in plain language, no protocol stack detail required.'
  },
};

// Convenience: attach items to registry node records.
// Usage in the diagnostic: node.question = CS_ITEMS[node.id] || null;
export function attachCSItems(nodes) {
  return nodes.map(n => (CS_ITEMS[n.id] ? { ...n, question: CS_ITEMS[n.id] } : n));
}
